import { openDB } from 'idb';

const DB_NAME = 'tech-learn-academy';
const USER_STORE = 'users';
const TOKEN_STORE = 'tokens';
const ENROLLMENT_STORE = 'enrollments';
const CONTACT_STORE = 'contact_messages';
const PAYMENT_STORE = 'payment_requests';
const DB_VERSION = 5; // Keep the existing version to maintain data

export const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db, oldVersion, newVersion) {
    // Handle all upgrades from any previous version
    if (oldVersion < 1) {
      if (!db.objectStoreNames.contains(USER_STORE)) {
        const userStore = db.createObjectStore(USER_STORE, {
          keyPath: 'id',
          autoIncrement: true
        });
        userStore.createIndex('email', 'email', { unique: true });
        userStore.createIndex('emailLookup', 'email', { unique: true });
      }
    }
    
    if (oldVersion < 2) {
      if (!db.objectStoreNames.contains(TOKEN_STORE)) {
        const tokenStore = db.createObjectStore(TOKEN_STORE, { keyPath: 'userId' });
        tokenStore.createIndex('userId', 'userId', { unique: true });
      }
    }

    if (oldVersion < 3) {
      if (!db.objectStoreNames.contains(ENROLLMENT_STORE)) {
        const enrollmentStore = db.createObjectStore(ENROLLMENT_STORE, { 
          keyPath: ['userId', 'courseId'] 
        });
        enrollmentStore.createIndex('userId', 'userId');
        enrollmentStore.createIndex('courseId', 'courseId');
      }
    }

    if (oldVersion < 4) {
      if (!db.objectStoreNames.contains(CONTACT_STORE)) {
        const contactStore = db.createObjectStore(CONTACT_STORE, {
          keyPath: 'id',
          autoIncrement: true
        });
        contactStore.createIndex('email', 'email');
        contactStore.createIndex('createdAt', 'createdAt');
      }
    }

    if (oldVersion < 5) {
      if (!db.objectStoreNames.contains(PAYMENT_STORE)) {
        const paymentStore = db.createObjectStore(PAYMENT_STORE, {
          keyPath: 'id',
          autoIncrement: true
        });
        paymentStore.createIndex('email', 'email');
        paymentStore.createIndex('status', 'status');
        paymentStore.createIndex('createdAt', 'createdAt');
      }
    }
  },
  blocked() {
    console.warn('Database upgrade blocked. Please close other tabs of this app and reload.');
  },
  blocking() {
    console.warn('This tab is blocking a newer version of the application. Please reload.');
  },
  terminated() {
    console.error('Database connection was terminated unexpectedly.');
  }
});

interface User {
  id?: number;
  email: string;
  password: string;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
}

export async function createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> {
  const db = await dbPromise;
  const tx = db.transaction([USER_STORE, TOKEN_STORE], 'readwrite');
  const userStore = tx.objectStore(USER_STORE);
  const tokenStore = tx.objectStore(TOKEN_STORE);
  
  try {
    // Check if user exists
    const existingUser = await userStore.index('emailLookup').get(userData.email);
    if (existingUser) {
      throw new Error('User already exists');
    }
    
    // Create new user
    const user: User = {
      ...userData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    const id = await userStore.add(user);
    
    // Initialize tokens for new user with 0 balance
    await tokenStore.add({
      userId: id,
      balance: 0,
      claimed: false,
      lastUpdated: new Date().toISOString()
    });
    
    await tx.done;
    return { ...user, id: id as number };
  } catch (error) {
    await tx.abort();
    throw error;
  }
}

export async function getUserByEmail(email: string): Promise<User | undefined> {
  const db = await dbPromise;
  const tx = db.transaction(USER_STORE, 'readonly');
  const store = tx.objectStore(USER_STORE);
  
  try {
    const user = await store.index('emailLookup').get(email);
    await tx.done;
    return user || undefined;
  } catch (error) {
    await tx.abort();
    throw error;
  }
}

export async function getUserById(id: number): Promise<User | undefined> {
  const db = await dbPromise;
  const tx = db.transaction(USER_STORE, 'readonly');
  const store = tx.objectStore(USER_STORE);
  
  try {
    const user = await store.get(id);
    await tx.done;
    return user || undefined;
  } catch (error) {
    await tx.abort();
    throw error;
  }
}

// Initialize the database connection
dbPromise.catch(error => {
  console.error('Failed to open database:', error);
});